/**
 * Session context: the bootstrap payload (user, offices, permissions,
 * entitlements, preferences) for everything under the shell, plus the
 * public `usePermission` and `useEntitlement` hooks.
 */
import { ROLE_MODULES } from '@velora/contracts/identity';
import type { ShellBootstrap } from '@velora/contracts/shell';
import { createContext, useContext, type ReactNode } from 'react';
import { useOfficeScope } from '../office/OfficeScope.js';
import {
  can,
  canInScope,
  entitlementFor,
  type EntitlementView,
  type PermissionMode,
} from './access.js';

const SessionContext = createContext<ShellBootstrap | null>(null);

export function SessionProvider({
  value,
  children,
}: {
  value: ShellBootstrap;
  children: ReactNode;
}) {
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

/** The signed-in session. Throws outside the shell (a programming error). */
export function useSession(): ShellBootstrap {
  const s = useContext(SessionContext);
  if (!s) throw new Error('useSession must be used inside the app shell');
  return s;
}

export function useOptionalSession(): ShellBootstrap | null {
  return useContext(SessionContext);
}

export interface UsePermissionOptions {
  /** Check one office instead of the current office scope. */
  locationId?: string | null;
  /** In "All offices": `any` office (default, mirrors the server) or `every` office. */
  mode?: PermissionMode;
}

/**
 * Whether the user may do `key` (`area.action`) in the current office scope.
 * Fails closed: no session or unknown key means false.
 */
export function usePermission(key: string, options: UsePermissionOptions = {}): boolean {
  const session = useOptionalSession();
  const { locationIds } = useOfficeScope();
  if (options.locationId !== undefined) return can(session, key, options.locationId);
  return canInScope(session, key, locationIds, options.mode);
}

/**
 * Marketplace state of a feature for the offices in scope. `null` (a core
 * module) is always active.
 */
export function useEntitlement(featureKey: string | null): EntitlementView {
  const session = useOptionalSession();
  const { locationIds } = useOfficeScope();
  return entitlementFor(session, featureKey, locationIds);
}

/**
 * True when the person's roles hide this page section (`<module>.<section>`,
 * ROLE_MODULES in @velora/contracts/identity: Settings > Roles > Module
 * access). The server refuses the section's API too; this only keeps the UI
 * from showing a tab that would answer "not included in your role".
 */
export function useSectionHidden(section: string): boolean {
  const session = useOptionalSession();
  return sectionHidden(session?.hiddenModules, section);
}

/**
 * True when the person's roles hide a whole module (ROLE_MODULES key, e.g.
 * 'analytics'). QA r57: Home kept asking /v1/analytics for people whose role
 * hides Analytics and got 403s; its cards and queries check this.
 */
export function useModuleHidden(key: string): boolean {
  const session = useOptionalSession();
  return session?.hiddenModules?.includes(key) === true;
}

/** `reports.view` that can actually reach the Analytics API (not hidden by the role). */
export function useReportsVisible(options: UsePermissionOptions = {}): boolean {
  const allowed = usePermission('reports.view', options);
  const hidden = useModuleHidden('analytics');
  return allowed && !hidden;
}

export function sectionHidden(hidden: readonly string[] | undefined, section: string): boolean {
  if (!hidden || hidden.length === 0) return false;
  return ROLE_MODULES.some((m) => hidden.includes(m.key) && m.sections.includes(section));
}
